import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { Check, Sparkles, CreditCard, X } from "lucide-react";
import { toast } from "sonner";

const PLUS_FEATURES = [
  "Premium assessment library",
  "Additional cognitive challenges",
  "Relationship assessments",
  "Love-language preferences",
  "Communication-style analysis",
  "Work-style assessments",
  "Decision-making challenges",
  "Progress tracking over time",
  "New assessments added monthly",
];

export default function Billing() {
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/subscriptions/me");
      setSub(data.subscription);
    } catch (e) { if (e?.response?.status === 401) nav("/auth"); }
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const portal = async () => {
    try {
      const { data } = await api.post("/subscriptions/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { toast.error("Portal unavailable"); }
  };

  const cancel = async () => {
    if (!confirm("Cancel at the end of the current billing period?")) return;
    try {
      await api.post("/subscriptions/cancel");
      toast.success("Cancelled at period end");
      load();
    } catch (e) { toast.error("Could not cancel"); }
  };

  const active = sub && ["active", "trialing"].includes(sub.status);

  return (
    <div><Nav />
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">Membership</div>
        <h1 className="font-serif text-4xl sm:text-5xl mb-3" data-testid="billing-title">DualQ Plus</h1>
        <p className="text-slate-400 max-w-xl mb-8">
          Keep exploring how your mind works. New assessments, deeper measurement, progress over time.
          <span className="block text-xs mt-2 text-slate-500 font-mono">The $1.99 report is one-time and separate — subscribing never auto-charges for a report, and buying a report never auto-subscribes you.</span>
        </p>

        {active ? (
          <div className="rounded-3xl bg-[#131E36] border border-emerald-500/30 p-6 sm:p-8 fade-up" data-testid="active-sub-card">
            <div className="uppercase font-mono text-xs tracking-[0.24em] text-emerald-300 mb-2">Active · DualQ Plus Monthly</div>
            <div className="font-serif text-3xl mb-2">$4.99 / month</div>
            {sub.current_period_end && (
              <p className="text-slate-400 text-sm">Renews on <span className="text-slate-200">{new Date(sub.current_period_end * 1000).toLocaleDateString()}</span>{sub.cancel_at_period_end && " · set to cancel at period end"}</p>
            )}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button onClick={portal} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B]" data-testid="manage-billing-btn">
                <CreditCard className="w-4 h-4" /> Manage billing & payment methods
              </button>
              {!sub.cancel_at_period_end && (
                <button onClick={cancel} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-700 hover:border-rose-500/50 text-slate-200" data-testid="cancel-sub-btn">
                  <X className="w-4 h-4" /> Cancel at period end
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-3xl bg-gradient-to-br from-[#0E172A] to-[#131E36] border border-[#E0A96D]/25 p-6 sm:p-8 fade-up relative overflow-hidden" data-testid="plus-offer-card">
            <div className="absolute -top-20 -right-20 w-[260px] h-[260px] rounded-full bg-[#E0A96D]/10 blur-[100px]" />
            <div className="relative">
              <div className="font-serif text-3xl mb-1">$4.99</div>
              <div className="text-xs text-slate-500 font-mono uppercase tracking-widest mb-4">per month · cancel anytime</div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-300 mb-6">
                {PLUS_FEATURES.map(f => (
                  <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /><span>{f}</span></li>
                ))}
              </ul>
              <button onClick={subscribe} disabled={loading}
                className="inline-flex items-center gap-2 px-6 py-4 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] disabled:opacity-60"
                data-testid="subscribe-btn">
                <Sparkles className="w-4 h-4" /> {loading ? "Starting checkout…" : "Subscribe to DualQ Plus"}
              </button>
              <p className="text-xs text-slate-500 font-mono mt-3">
                Monthly billing of $4.99 USD until you cancel. You'll be charged every month on the anniversary of today.
                Secure checkout via Stripe. No lock-in.
              </p>
            </div>
          </div>
        )}

        <div className="mt-10 text-xs text-slate-500 border-t border-slate-800/60 pt-5">
          <strong className="text-slate-300">What this is not:</strong> DualQ Plus is not a scientifically validated clinical assessment. Items are EXPERIMENTAL. Scores are provisional.
        </div>
      </div>
    </div>
  );
}
